/* eslint-disable import/no-cycle */
import VueCookie from 'vue-cookie';
import axios, { AxiosInstance } from 'axios';
import { ENDPOINTS } from '@/settings/endpoints';
import { ActivityItemStatus } from '@/components/grades/types';
import { GradesHelper } from '@/helpers/assessments/grades';

const d = VueCookie.get('d', { domain: '.descomplica.com.br' });

export interface CollaborationResponse<T> {
  data: T;
  message: string;
  identifier: string;
}

export interface ActivityItem {
  exerciseId: string;
  disciplineSlug: string;
  contentGroupId: string;
  contentGroupSlug: string;
  status: ActivityItemStatus | string;
}

export interface GradeComponentDetails {
  grade: number;
  maxGrade: number;
}

export interface UserBaseGrades {
  grade: number;
}

export interface UserActivitiesDetails extends UserBaseGrades {
  gradeComponents?: {
    achievement: GradeComponentDetails | null;
    objectiveExercise: GradeComponentDetails | null;
    discursiveExercise?: GradeComponentDetails | null;
    practiceExercise?: GradeComponentDetails | null;
    genericDiscursiveExercise?: GradeComponentDetails | null;
    reviewList?: GradeComponentDetails | null;
    exam?: GradeComponentDetails | null;
  };
  activitiesStatus?: {
    objectiveExercises: Array<ActivityItem>;
    discursiveExercises?: Array<ActivityItem> | null;
    practiceExercises?: Array<ActivityItem> | null;
    genericDiscursiveExercises?: Array<ActivityItem> | null;
    reviewLists: Array<ActivityItem>;
    exam: Array<ActivityItem> | null;
  };
}

export interface UserPersistedGrade extends UserBaseGrades {
  userId: string;
  classroomId: number;
  objectiveExercise: number;
  discursiveExercise: number;
  practiceExercise: number;
  reviewList: number;
  achievement: number;
  exam: number;
  replacementExam: number;
}

const encodeAssertionId = (id: number): string => btoa(id.toString().split('').reverse().join(''));

const decodeAssertionId = (id: string): number =>
  Number.parseInt(atob(id).split('').reverse().join(''), 10);

export interface UserListActivityStatus {
  listId?: number;
  status: ActivityItemStatus;
  answeredAt?: Date;
  dueDate?: Date;
  isOnLastDeliverable: boolean;
  cancelledExercisesIds?: string[];
}

export interface UserListStatus {
  objectiveExercises?: UserListActivityStatus;
  discursiveExercises?: UserListActivityStatus;
  practiceExercises?: UserListActivityStatus;
  reviewLists?: UserListActivityStatus;
}

export interface ActiveStudyWindow {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isRunning?: boolean;
}

export class UserGradesService {
  private pepeLegal: AxiosInstance;

  constructor() {
    this.pepeLegal = axios.create({
      baseURL: ENDPOINTS.pegasusPepeLegal,
      timeout: 10000,
      headers: {
        Authorization: `Bearer ${d}`,
      },
    });
  }

  async getPersistedDisciplineGrade(
    disciplineInstanceId: string,
    isDisciplineMigrated: boolean,
    disciplineType: string = 'REGULAR',
  ): Promise<UserActivitiesDetails> {
    const params = isDisciplineMigrated
      ? { classroomId: disciplineInstanceId }
      : { disciplineInstanceId };

    const { data: collabResponse } = await this.pepeLegal.get<
      CollaborationResponse<UserPersistedGrade>
    >(`/persisted-grades`, { params });

    // ToDo: This should be done on the backend. It must be improved in future releases.
    return GradesHelper.normalizeGradeDetails(collabResponse.data, disciplineType);
  }

  async getDisciplineGrades(disciplineInstanceId: string): Promise<UserActivitiesDetails> {
    const { data: collabResponse } = await this.pepeLegal.get<
      CollaborationResponse<UserActivitiesDetails>
    >(`/user-activities-details`, { params: { disciplineInstanceId } });

    return collabResponse.data;
  }

  async getAssertionsCorrections(assertionIds: number[]): Promise<any> {
    const assertionsParam = assertionIds.map(encodeAssertionId).join('|');

    const { data: collabResponse } = await this.pepeLegal.get<CollaborationResponse<any>>(
      `/questions/assertions-corrections`,
      {
        params: { assertions: assertionsParam },
      },
    );

    const assertionsDecoded = collabResponse.data.map(({ id, correct }: any) => ({
      id: decodeAssertionId(id),
      correct,
    }));

    return assertionsDecoded;
  }

  async getExercisesStatus({
    lessonId,
    disciplineInstanceId,
    reviewListId,
  }: {
    disciplineInstanceId?: string;
    lessonId?: string;
    reviewListId?: number;
  }): Promise<UserListStatus> {
    if (!disciplineInstanceId) return {};

    if (['cloylslhm2ful0ciz3cx84zy3', 'cloylrm9u2egt0biw98wk17o7'].includes(disciplineInstanceId)) {
      // This was necessary to attend a very specific condition/business decision for handling
      // these disciplines in a strict window (23.4). Sorry for the unpleasant view.
      return {};
    }

    const { data: collabResponse } = await this.pepeLegal.get('/user-lesson-status', {
      params: {
        lessonId,
        disciplineInstanceId,
        reviewListId,
      },
    });

    return {
      objectiveExercises: collabResponse.data.objectiveExercises[0],
      discursiveExercises: collabResponse.data.discursiveExercises[0],
      practiceExercises: collabResponse.data.practiceExercises[0],
      reviewLists: collabResponse.data.reviewLists[0],
    };
  }

  async getActiveStudyWindows(): Promise<ActiveStudyWindow[]> {
    const { data: collabResponse } = await this.pepeLegal.get<
      CollaborationResponse<ActiveStudyWindow[]>
    >('/active-study-windows');

    return collabResponse.data;
  }
}
